import React, { useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { get } from 'lodash';
import classNames from 'classnames';
import Box from 'core/components/Box';
import Checkbox from 'core/components/Checkbox';
import Flex from 'core/components/Flex';
import Text from 'core/components/Text';
import Icon from 'core/components/Icon';
import styled from 'styled-components';
import Tooltip from 'core/components/Tooltip';
import CELL_ACTIONS from './CELL_ACTIONS';
import CELL_TYPES from './CELL_TYPES';
import { getCellStyle, getColKey, getLastResizableColumnIndex, isResizableColumn } from './Cell';

// Refresh body cell NodeList every N frames while dragging to avoid querying each frame
const CELL_QUERY_INTERVAL = 15;

const StyledSortIcon = styled(Icon)`
  transition: transform 0.2s ease-out;
  transform: ${(props) => (props.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const ResizeHandle = styled.div`
  width: 10px;
  cursor: col-resize;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  z-index: 1;
  border-right: 1px solid ${({ theme }) => theme.borderColors.thin};
`;

const SelectAllCheckbox = (props) => {
  const { collection, onSelectAll } = props;

  return (
    <Checkbox
      aria-label="Select all"
      indeterminate={
        !!(
          collection.selected &&
          collection.selected.length > 0 &&
          collection.selected.length < collection.models.length
        )
      }
      key="select"
      checked={!!collection.isAllSelected}
      onChange={() => null}
      onClick={(e) => {
        e.stopPropagation();
        if (onSelectAll) {
          onSelectAll(e, collection);
        } else {
          collection.selectAll();
        }
      }}
    />
  );
};

const HeaderCell = (props) => {
  const {
    $setup,
    collection,
    column,
    columns,
    index,
    useSortTooltips,
    noHighlightSorted,
    onSort,
    onSelectAll,
    resizedColumns,
    tabPrefId,
    persistColumnWidths = true,
    columnWidthsRef,
    setIsResizing,
    updateGrid
  } = props;
  const headerCellRef = useRef(null);
  const tableContainerRef = useRef(null);

  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const resizingRef = useRef(false);
  const rafPendingRef = useRef(false);
  const lastAppliedWidthRef = useRef(column.width || columnWidthsRef?.[getColKey(column)] || null);
  const ignoreClickAfterResizeRef = useRef(false);
  const columnCellsRef = useRef([]);
  const frameCounterRef = useRef(0);

  const { field, direction } = collection.sortState;
  const sortable = column.sortable !== false && column.type !== CELL_TYPES.ACTION;
  const hasOnHeaderClick = typeof column.onHeaderClick === 'function';
  const interactive = sortable || hasOnHeaderClick;
  const serverSortable = column.serverSortable === true && column.type !== CELL_TYPES.ACTION;
  const sorted = field && (field === column.name || field === column.sortField);
  const columnName = column.sortField || column.name;
  const selectAllCheckbox = (column.actions && column.actions.includes(CELL_ACTIONS.SELECT)) || column.customSelect;
  const hasRightResizableColumns = columns.slice(index + 1).some(isResizableColumn);
  const isResizable = column.resizable !== false && column.type !== CELL_TYPES.ACTION && column.key !== 'select';

  const onClick = () => {
    // Skip sort immediately after resize to avoid accidental sorts
    if (ignoreClickAfterResizeRef.current) {
      ignoreClickAfterResizeRef.current = false;
      return;
    }
    if (sortable) {
      collection[serverSortable ? 'serverSort' : 'sort'](columnName);

      // serverSort provides serverSortFn for callbacks; for sync sorting we use onSort
      if (onSort) {
        onSort({ field: collection.sortState.field, direction: collection.sortState.direction });
      }
    }
    if (hasOnHeaderClick) {
      column.onHeaderClick(column);
    }
  };

  const calculateSpannedWidth = (startIndex, colSpan) => {
    let totalWidth = 0;
    for (let j = startIndex; j < startIndex + colSpan && j < columns.length; j += 1) {
      const spannedCol = columns[j];
      const spannedKey = getColKey(spannedCol);
      const resizedWidth =
        (columnWidthsRef && columnWidthsRef[spannedKey]) || (resizedColumns && resizedColumns[spannedKey]);

      if (resizedWidth) {
        totalWidth += resizedWidth;
      } else if (spannedCol.width) {
        totalWidth += spannedCol.width;
      } else {
        totalWidth += spannedCol.flexBasis || 100;
      }
    }
    return totalWidth;
  };

  const lockWidth = (el, w) => {
    el.style.width = `${w}px`;
    el.style.minWidth = `${w}px`;
    el.style.maxWidth = `${w}px`;
    el.style.flexBasis = `${w}px`;
    el.style.flexGrow = '0';
    el.style.flexShrink = '0';
  };

  const fillWidth = (el, w) => {
    el.style.width = '';
    el.style.minWidth = `${w}px`;
    el.style.maxWidth = '';
    el.style.flexBasis = `${w}px`;
    el.style.flexGrow = '1';
    el.style.flexShrink = '0';
  };

  const updateSpanningCells = () => {
    if (!columns || !resizedColumns) {
      return;
    }

    for (let i = 0; i <= index; i += 1) {
      const col = columns[i];

      const totalRowColSpan = typeof col.colSpan === 'function' ? col.colSpan(null, true) : col.colSpan || 1;

      const overlayColSpan =
        typeof col.colSpan === 'function' ? col.colSpan({ isOverlay: true, get: () => true }, false) : col.colSpan || 1;

      const spanningColKey = getColKey(col);

      const totalRowSpansResized = totalRowColSpan > 1 && i + totalRowColSpan > index;
      const overlaySpansResized = overlayColSpan > 1 && i + overlayColSpan > index;

      if (!totalRowSpansResized && !overlaySpansResized) {
        /* eslint-disable no-continue */
        continue;
      }

      // calculate widths for each case (only if they span the resized column)
      const totalRowWidth = totalRowSpansResized ? calculateSpannedWidth(i, totalRowColSpan) : null;
      const overlayWidth = overlaySpansResized ? calculateSpannedWidth(i, overlayColSpan) : null;

      const container = tableContainerRef.current || document;
      const spanningCells = container.querySelectorAll(`[data-col-key="${CSS.escape(spanningColKey)}"]`);

      spanningCells.forEach((cellEl) => {
        const row = cellEl.closest('.tr');
        if (!row) {
          return;
        }

        const isTotalRow = row.classList.contains('total');

        let widthToApply = null;

        if (isTotalRow && totalRowWidth !== null) {
          widthToApply = totalRowWidth;
        } else if (!isTotalRow) {
          // check if this is an overlay row by looking for hidden cells
          const isOverlayRow =
            row.querySelector('[style*="display: none"]') || row.querySelector('[style*="display:none"]');
          if (isOverlayRow && overlayWidth !== null) {
            widthToApply = overlayWidth;
          }
        }

        if (widthToApply !== null) {
          lockWidth(cellEl, widthToApply);
        }
      });
    }
  };

  const saveResizedColumns = () => {
    if (tabPrefId && persistColumnWidths) {
      const tabPrefs = get($setup, 'settings.tabPrefs') || {};
      if (!tabPrefs.resizedColumns) {
        tabPrefs.resizedColumns = {};
      }
      if (!tabPrefs.resizedColumns[tabPrefId]) {
        tabPrefs.resizedColumns[tabPrefId] = {};
      }
      tabPrefs.resizedColumns[tabPrefId] = {
        ...tabPrefs.resizedColumns[tabPrefId],
        ...resizedColumns
      };
      $setup.updateSettings({ tabPrefs });
    }
  };

  const applyWidth = (w) => {
    const el = headerCellRef.current;
    if (!el || lastAppliedWidthRef.current === w) {
      return;
    }

    const applyFn = hasRightResizableColumns ? lockWidth : fillWidth;
    applyFn(el, w);

    for (const cellEl of columnCellsRef.current) {
      if (cellEl) {
        applyFn(cellEl, w);
      }
    }

    lastAppliedWidthRef.current = w;
    updateSpanningCells();
  };

  const pointerMoveHandler = (e) => {
    if (!resizingRef.current) {
      return;
    }
    const diff = e.clientX - startXRef.current;
    const minWidth = Math.max(column.minWidth || 0, 50);
    const columnMaxWidth = column.maxWidth || Infinity;

    const constrainedDiff = Math.min(
      Math.max(diff, minWidth - startWidthRef.current),
      columnMaxWidth - startWidthRef.current
    );
    const newWidth = startWidthRef.current + constrainedDiff;

    const columnKey = getColKey(column);
    if (columnKey && resizedColumns) {
      resizedColumns[columnKey] = newWidth;
      if (columnWidthsRef) {
        columnWidthsRef[columnKey] = newWidth;
      }
    }

    if (!rafPendingRef.current) {
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        // Periodically refresh list of visible cells (virtualization may reuse nodes)
        frameCounterRef.current += 1;
        if (frameCounterRef.current % CELL_QUERY_INTERVAL === 0 || columnCellsRef.current.length === 0) {
          const key = getColKey(column);
          const container = tableContainerRef.current || document;
          columnCellsRef.current = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(key)}"]`));
        }
        applyWidth(newWidth);
      });
    }
  };

  const pointerUpHandler = (e) => {
    if (!resizingRef.current) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    document.removeEventListener('pointermove', pointerMoveHandler, true);
    document.removeEventListener('pointerup', pointerUpHandler, true);
    document.body.style.userSelect = '';
    resizingRef.current = false;
    if (setIsResizing) {
      setIsResizing(false);
    }
    ignoreClickAfterResizeRef.current = true;
    saveResizedColumns();
    if (updateGrid) {
      updateGrid();
    }
  };

  const handlePointerDown = (e) => {
    if (!isResizable) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    if (!tableContainerRef.current) {
      tableContainerRef.current = headerCellRef.current?.closest('[role="table"]');
    }

    const rect = headerCellRef.current.getBoundingClientRect();
    startXRef.current = e.clientX;
    startWidthRef.current = rect.width;
    resizingRef.current = true;
    if (setIsResizing) {
      setIsResizing(true);
    }
    document.body.style.userSelect = 'none';

    // capture all current column widths from DOM before resize starts and lock resizable columns
    // to fixed widths during drag. non-resizable columns and the last resizable column are skipped.
    if (columns && columnWidthsRef) {
      const headerRow = headerCellRef.current?.closest('.tr');
      if (headerRow) {
        const headerCells = headerRow.querySelectorAll('.th');
        const lastResizableIdx = getLastResizableColumnIndex(columns);

        headerCells.forEach((cell, idx) => {
          if (idx < columns.length) {
            const col = columns[idx];
            const colKey = getColKey(col);
            if (colKey) {
              if (!isResizableColumn(col)) {
                return;
              }

              const cellRect = cell.getBoundingClientRect();
              const { width } = cellRect;
              // resizedColumns is persisted and only the dragged column should be saved.
              columnWidthsRef[colKey] = width;

              if (idx === lastResizableIdx) {
                return;
              }

              // lock all other columns to fixed width during drag
              lockWidth(cell, width);

              // lock body cells for this column (skip spanning cells - they'll be fixed below)
              const container = tableContainerRef.current || document;
              const bodyCells = container.querySelectorAll(`[data-col-key="${CSS.escape(colKey)}"]`);
              bodyCells.forEach((bodyCell) => {
                // skip cells in rows that have spanning behavior (TotalRow or overlay rows)
                const row = bodyCell.closest('.tr');
                if (row) {
                  const isTotalRow = row.classList.contains('total');
                  const isOverlayRow =
                    row.querySelector('[style*="display: none"]') || row.querySelector('[style*="display:none"]');
                  if (isTotalRow || isOverlayRow) {
                    return; // skip - these will be handled by updateSpanningCells
                  }
                }
                lockWidth(bodyCell, width);
              });
            }
          }
        });
      }
    }

    updateSpanningCells();

    const key = getColKey(column);
    const container = tableContainerRef.current || document;
    columnCellsRef.current = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(key)}"]`));

    document.addEventListener('pointermove', pointerMoveHandler, true);
    document.addEventListener('pointerup', pointerUpHandler, true);
  };

  const handleHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick();
    }
  };

  const className = classNames('th', { sortable, sorted, interactive, noHighlightSorted });

  const style = getCellStyle({ ...props, resizedColumns });

  const align = column.headerAlign || column.align;
  const padding = column.headerPadding ?? column.padding ?? '6px 0 6px 12px';
  const justifyContent = column.flexDirection !== 'column' ? column.justifyContent : undefined;
  const dirString = sorted && direction === 'asc' ? 'Ascending' : 'Descending';
  const ariaProps = {};
  if (dirString) {
    ariaProps['aria-sort'] = dirString;
  }
  let labelGuess = column.label || column.name;
  if (labelGuess?.length === 0 && column.type === 'action') {
    labelGuess = 'action options';
  }
  ariaProps['aria-label'] = labelGuess;
  return (
    <Flex
      {...ariaProps}
      ref={headerCellRef}
      className={className}
      onClick={onClick}
      css={{ cursor: interactive ? 'pointer' : 'default', position: 'relative' }}
      style={style}
      alignItems="flex-end"
      justifyContent={justifyContent || (align === 'right' ? 'flex-end' : align)}
      p={padding}
      role="columnheader"
      data-col-key={getColKey(column)}
      onKeyDown={handleHeaderKeyDown}
      overflow="hidden"
    >
      {selectAllCheckbox && (
        <SelectAllCheckbox collection={collection} onSelectAll={onSelectAll} tabIndex={interactive ? 0 : -1} />
      )}
      {!sorted && <Text tabIndex={interactive ? 0 : -1}>{column.label || ''}</Text>}
      {sorted && (column.label || '')}
      {sorted && !useSortTooltips && (
        <StyledSortIcon icon="chevron-up" direction={direction} tabIndex={interactive ? 0 : -1} />
      )}
      {sorted && useSortTooltips && (
        <Tooltip content={<Text>{dirString}</Text>}>
          <StyledSortIcon icon="chevron-up" direction={direction} tabIndex={interactive ? 0 : -1} />
        </Tooltip>
      )}
      {!sorted && !selectAllCheckbox && sortable && align !== 'right' && <Box width={16} />}
      {tabPrefId && resizedColumns && isResizable && <ResizeHandle onPointerDown={handlePointerDown} />}
    </Flex>
  );
};

export default inject('$setup')(observer(HeaderCell));
